/**
 * D3 -- what the chosen time actually rewinds.
 *
 * The cascade offers a date picker, and half the page ignores it. That
 * is not a bug in the backend: chain reads replay at the timestamp,
 * while findings and foundation entities are current-state -- a
 * compromise the cascade module has documented since P2.6. It is a bug
 * on the SURFACE, which showed a date and let a reader believe the
 * whole page had moved to it.
 *
 * Asking for 1 August on this estate returns fourteen impacts, every
 * one headlined by a finding raised in September. Saying so is the
 * difference between a limitation and a lie.
 */
import { useTranslation } from "react-i18next";
import { formatDateTime } from "shared/time/formatDate";

export function AsOfBanner({
  asOf,
  onReturnToLive,
}: {
  asOf: string | null;
  /**
   * D11 -- offered where the reader can get back. The constellation's
   * scrubber moved the whole picture and said nothing, so an examiner
   * could be looking at March while believing he was looking at today,
   * with no way back that did not involve guessing.
   */
  onReturnToLive?: () => void;
}) {
  const { t, i18n } = useTranslation();
  // Nothing to explain while the page is showing now.
  if (!asOf) return null;
  return (
    <p
      data-testid="as-of-banner"
      className="flex flex-wrap items-center gap-2 rounded border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-900"
    >
      <span>{t("asOfBanner.text", { when: formatDateTime(asOf, i18n.language) })}</span>
      {onReturnToLive && (
        <button
          type="button"
          data-testid="as-of-return-to-live"
          onClick={onReturnToLive}
          className="font-semibold text-brand-brassDark hover:underline"
        >
          {t("asOfBanner.returnToLive")}
        </button>
      )}
    </p>
  );
}

/**
 * Is this row outside the chosen window -- i.e. something the date did
 * NOT rewind?
 */
export function isAfterAsOf(raisedAt: string | null, asOf: string | null): boolean {
  if (!asOf || !raisedAt) return false;
  return new Date(raisedAt).getTime() > new Date(asOf).getTime();
}
